import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FileUp, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { MobileDialog } from "../mobile";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const CONNECTION_OPTIONS = [
  {
    connection_type: "bank",
    label: "Bank account",
    description: "Import transactions for review. No payments or transfers.",
  },
  {
    connection_type: "business_email",
    label: "Business email",
    description: "Prepare email intake permissions. No email is sent during onboarding.",
  },
  {
    connection_type: "whatsapp",
    label: "WhatsApp / SMS",
    description: "Prepare the messaging connection. Connecting alone cannot contact anyone.",
  },
  {
    connection_type: "calendar",
    label: "Existing calendar",
    description: "Prepare diary import and conflict checks without writing to the live NDG diary.",
  },
  {
    connection_type: "growth_advertising",
    label: "Growth Advertising",
    description: "Prepare campaign setup. No advert is published and no money can be spent.",
  },
  {
    connection_type: "video_meetings",
    label: "Video meetings",
    description: "Prepare Zoom-style session delivery. No meeting is created.",
  },
];

const EMPTY_TEMPLATE = {
  label: "PDI onboarding package",
  source_summary: "Historical and future diary evidence supplied by the instructor.",
  pupils: [],
  bookings: [],
  calendar_blocks: [],
  business_records: [],
  connection_preferences: CONNECTION_OPTIONS.map((option) => ({
    connection_type: option.connection_type,
    setup_requested: false,
    status: "not_selected",
    automatic_authority_granted: false,
    permission_summary: option.description,
  })),
  rules: {
    minimum_future_bookings: 3,
    normal_anchor_times: ["07:45", "10:15", "12:45", "15:15", "17:45"],
    route_time_is_dynamic: true,
    revenue_before_shorter_day: true,
  },
};

const EMPTY_PUPIL = {
  external_ref: "",
  name: "",
  phone: "",
  email: "",
  primary_address: "",
  home_postcode: "",
  area_label: "",
  normal_duration_minutes: 120,
  preferred_days: [],
  preferred_times: [],
  minimum_future_bookings: 3,
  minimum_booking_exempt: false,
  exemption_reason: "",
  test_ready: false,
  notes: "",
};

const EMPTY_BOOKING = {
  pupil_ref: "",
  date: "",
  start_time: "07:45",
  duration_minutes: 120,
  status: "booked",
  is_test_day: false,
  fixed: false,
  pickup_address: "",
  return_address: "",
  source: "instructor_onboarding",
};

const EMPTY_CALENDAR_BLOCK = {
  block_type: "away",
  start_date: "",
  end_date: "",
  start_time: "07:45",
  end_time: "19:45",
  all_day: true,
  fixed: true,
  label: "",
  source: "instructor_onboarding",
};

const EMPTY_BUSINESS_RECORD = {
  record_type: "expense",
  date: "",
  amount_pence: null,
  description: "",
  evidence_id: "",
  original_filename: "",
  document_status: "not_required",
  permission_scope: "private",
  source: "instructor_onboarding",
};

const inputClass =
  "mt-1 h-11 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 text-sm text-white outline-none focus:border-sky-500";
const labelClass = "text-[11px] font-bold uppercase tracking-wide text-slate-400";

async function sha256OfFile(file) {
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function cleanOptionalFields(value) {
  if (Array.isArray(value)) return value.map(cleanOptionalFields);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      typeof item === "string" && !item.trim() ? null : cleanOptionalFields(item),
    ]),
  );
}

export default function PDIReplicaLab() {
  const [open, setOpen] = useState(false);
  const [datasets, setDatasets] = useState([]);
  const [draft, setDraft] = useState(() => structuredClone(EMPTY_TEMPLATE));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [payload, setPayload] = useState(() => JSON.stringify(EMPTY_TEMPLATE, null, 2));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [activationPlans, setActivationPlans] = useState({});
  const [planningId, setPlanningId] = useState(null);
  const [businessFiles, setBusinessFiles] = useState({});
  const [uploadingRecord, setUploadingRecord] = useState(null);
  const businessFileInputRefs = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/pdi-replica/datasets`, {
        withCredentials: true,
      });
      setDatasets(data.datasets || []);
    } catch (error) {
      toast.error("Couldn't load onboarding packages", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  async function continueDraft(dataset) {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/pdi-replica/datasets/${dataset.id}`,
        { withCredentials: true },
      );
      const storedPreferences = data.connection_preferences || [];
      setDraft({
        label: data.label || EMPTY_TEMPLATE.label,
        source_summary: data.source_summary || "",
        pupils: data.pupils || [],
        bookings: data.bookings || [],
        calendar_blocks: data.calendar_blocks || [],
        business_records: data.business_records || [],
        connection_preferences: CONNECTION_OPTIONS.map((option) => {
          const stored = storedPreferences.find(
            (item) => item.connection_type === option.connection_type,
          );
          return stored || {
            connection_type: option.connection_type,
            setup_requested: false,
            status: "not_selected",
            automatic_authority_granted: false,
            permission_summary: option.description,
          };
        }),
        rules: data.rules || structuredClone(EMPTY_TEMPLATE.rules),
      });
      setEditingId(dataset.id);
      setBusinessFiles({});
      setAdvancedOpen(false);
      toast.success("Private draft reopened");
    } catch (error) {
      toast.error("Couldn't reopen that onboarding draft", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  }

  function startFreshDraft() {
    setDraft(structuredClone(EMPTY_TEMPLATE));
    setEditingId(null);
    setBusinessFiles({});
    setAdvancedOpen(false);
  }

  function updateDraft(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function addPupil() {
    setDraft((current) => ({
      ...current,
      pupils: [...current.pupils, { ...EMPTY_PUPIL }],
    }));
  }

  function updatePupil(index, field, value) {
    setDraft((current) => ({
      ...current,
      pupils: current.pupils.map((pupil, pupilIndex) =>
        pupilIndex === index ? { ...pupil, [field]: value } : pupil,
      ),
    }));
  }

  function removePupil(index) {
    setDraft((current) => ({
      ...current,
      pupils: current.pupils.filter((_, pupilIndex) => pupilIndex !== index),
    }));
  }

  function addBooking() {
    const firstPupil = draft.pupils[0];
    setDraft((current) => ({
      ...current,
      bookings: [
        ...current.bookings,
        {
          ...EMPTY_BOOKING,
          pupil_ref: firstPupil?.external_ref || firstPupil?.name || "",
        },
      ],
    }));
  }

  function updateBooking(index, field, value) {
    setDraft((current) => ({
      ...current,
      bookings: current.bookings.map((booking, bookingIndex) =>
        bookingIndex === index ? { ...booking, [field]: value } : booking,
      ),
    }));
  }

  function removeBooking(index) {
    setDraft((current) => ({
      ...current,
      bookings: current.bookings.filter((_, bookingIndex) => bookingIndex !== index),
    }));
  }

  function addCalendarBlock() {
    setDraft((current) => ({
      ...current,
      calendar_blocks: [
        ...(current.calendar_blocks || []),
        { ...EMPTY_CALENDAR_BLOCK },
      ],
    }));
  }

  function updateCalendarBlock(index, field, value) {
    setDraft((current) => ({
      ...current,
      calendar_blocks: (current.calendar_blocks || []).map((block, blockIndex) =>
        blockIndex === index ? { ...block, [field]: value } : block,
      ),
    }));
  }

  function removeCalendarBlock(index) {
    setDraft((current) => ({
      ...current,
      calendar_blocks: (current.calendar_blocks || []).filter(
        (_, blockIndex) => blockIndex !== index,
      ),
    }));
  }

  function addBusinessRecord() {
    setDraft((current) => ({
      ...current,
      business_records: [
        ...(current.business_records || []),
        { ...EMPTY_BUSINESS_RECORD },
      ],
    }));
  }

  function updateBusinessRecord(index, field, value) {
    setDraft((current) => ({
      ...current,
      business_records: (current.business_records || []).map((record, recordIndex) =>
        recordIndex === index ? { ...record, [field]: value } : record,
      ),
    }));
  }

  function removeBusinessRecord(index) {
    setDraft((current) => ({
      ...current,
      business_records: (current.business_records || []).filter(
        (_, recordIndex) => recordIndex !== index,
      ),
    }));
    setBusinessFiles((current) => {
      const next = { ...current };
      delete next[index];
      return next;
    });
  }

  function selectBusinessFile(index, file) {
    if (!file) return;
    setBusinessFiles((current) => ({ ...current, [index]: file }));
    updateBusinessRecord(index, "original_filename", file.name);
    updateBusinessRecord(index, "evidence_id", "");
    updateBusinessRecord(index, "document_status", "pending");
  }

  async function uploadBusinessDocument(index) {
    const file = businessFiles[index];
    const record = (draft.business_records || [])[index];
    if (!file || !record) {
      toast.error("Choose a document first");
      return;
    }
    if (!record.description?.trim()) {
      toast.error("Add a description before uploading");
      return;
    }

    setUploadingRecord(index);
    try {
      const sha256 = await sha256OfFile(file);
      const init = await axios.post(
        `${API}/api/universal_intent/ui_s3/batch/init`,
        {
          purpose_code: "onboarding_business_record",
          purpose_label: record.description.trim(),
        },
        { withCredentials: true },
      );
      const batchId = init.data.batch_id;
      const manifest = [{
        filename: file.name,
        byte_size: file.size,
        sha256,
        mime_type: file.type || "application/octet-stream",
        capture_date_iso: file.lastModified
          ? new Date(file.lastModified).toISOString()
          : null,
        camera_role_hint: null,
      }];
      const checked = await axios.post(
        `${API}/api/universal_intent/ui_s3/batch/${batchId}/enumerate`,
        { manifest },
        { withCredentials: true },
      );

      let evidenceId = checked.data.duplicate_files?.[0]?.existing_evidence_id || "";
      if (checked.data.new_files?.length) {
        const chunkSize = checked.data.chunk_size || 25 * 1024 * 1024;
        const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
          const start = chunkIndex * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          await axios.put(
            `${API}/api/universal_intent/ui_s3/batch/${batchId}/chunk/${sha256}/${chunkIndex}?total_chunks=${totalChunks}`,
            file.slice(start, end),
            {
              withCredentials: true,
              headers: { "Content-Type": "application/octet-stream" },
            },
          );
        }
        const finalised = await axios.post(
          `${API}/api/universal_intent/ui_s3/batch/${batchId}/finalize/${sha256}`,
          {},
          { withCredentials: true },
        );
        if (!["imported", "duplicate"].includes(finalised.data.status)) {
          throw new Error(finalised.data.reason || "Document verification failed");
        }
        evidenceId =
          finalised.data.evidence_id ||
          finalised.data.existing_evidence_id ||
          "";
      }
      await axios.post(
        `${API}/api/universal_intent/ui_s3/batch/${batchId}/complete`,
        {},
        { withCredentials: true },
      );
      if (!evidenceId) throw new Error("The document was verified but no evidence reference was returned");

      updateBusinessRecord(index, "evidence_id", evidenceId);
      updateBusinessRecord(index, "original_filename", file.name);
      updateBusinessRecord(index, "document_status", "uploaded");
      toast.success("Private business document uploaded", {
        description: "Owner-only evidence · not shared with pupils or the live diary",
      });
    } catch (error) {
      updateBusinessRecord(index, "document_status", "pending");
      toast.error("Couldn't upload that business document", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setUploadingRecord(null);
    }
  }

  function updateConnectionPreference(connectionType, requested) {
    setDraft((current) => ({
      ...current,
      connection_preferences: CONNECTION_OPTIONS.map((option) => {
        const existing = (current.connection_preferences || []).find(
          (preference) => preference.connection_type === option.connection_type,
        );
        if (option.connection_type !== connectionType) {
          return existing || {
            connection_type: option.connection_type,
            setup_requested: false,
            status: "not_selected",
            automatic_authority_granted: false,
            permission_summary: option.description,
          };
        }
        return {
          ...(existing || {}),
          connection_type: option.connection_type,
          setup_requested: requested,
          status: requested ? "requested" : "not_selected",
          automatic_authority_granted: false,
          permission_summary: option.description,
        };
      }),
    }));
  }

  function openAdvancedJson() {
    setPayload(JSON.stringify(cleanOptionalFields(draft), null, 2));
    setAdvancedOpen(true);
  }

  function applyAdvancedJson() {
    try {
      const parsed = JSON.parse(payload);
      setDraft(parsed);
      setAdvancedOpen(false);
      toast.success("Advanced data applied to the private draft");
    } catch {
      toast.error("Advanced JSON is not valid");
    }
  }

  async function saveReplica() {
    if (!draft.label.trim()) {
      toast.error("Give the onboarding package a name");
      return;
    }
    if (!draft.pupils.length) {
      toast.error("Add at least one pupil before saving");
      return;
    }
    if (draft.pupils.some((pupil) => !pupil.name?.trim())) {
      toast.error("Every pupil needs a name");
      return;
    }
    if (
      draft.bookings.some(
        (booking) => !booking.pupil_ref || !booking.date || !booking.start_time,
      )
    ) {
      toast.error("Every booking needs a pupil, date and start time");
      return;
    }

    if (
      (draft.calendar_blocks || []).some(
        (block) =>
          !block.start_date ||
          (!block.all_day && (!block.start_time || !block.end_time)),
      )
    ) {
      toast.error("Every diary block needs a date and, unless all-day, start and end times");
      return;
    }
    if (
      (draft.business_records || []).some(
        (record) => !record.description?.trim(),
      )
    ) {
      toast.error("Every business record needs a description");
      return;
    }

    setSaving(true);
    try {
      const requestPayload = cleanOptionalFields(draft);
      const { data } = editingId
        ? await axios.put(
          `${API}/api/pdi-replica/datasets/${editingId}`,
          requestPayload,
          { withCredentials: true },
        )
        : await axios.post(
          `${API}/api/pdi-replica/datasets`,
          requestPayload,
          { withCredentials: true },
        );
      toast.success(
        editingId
          ? "Private onboarding package updated"
          : "Private onboarding package saved",
        {
          description: `${data.pupil_count} pupils · ${data.booking_count} bookings · no invitations`,
        },
      );
      setDraft(structuredClone(EMPTY_TEMPLATE));
      setEditingId(null);
      setBusinessFiles({});
      await refresh();
    } catch (error) {
      toast.error("Couldn't save onboarding package", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setSaving(false);
    }
  }

  function validationIssues(dataset) {
    const preview = dataset.activation_preview || {};
    const issues = [];
    const add = (items, singular, plural = `${singular}s`) => {
      const count = (items || []).length;
      if (count) issues.push(`${count} ${count === 1 ? singular : plural}`);
    };

    if (!preview.pupil_count) issues.push("No pupils added");
    add(preview.missing_contact, "missing contact");
    add(preview.missing_location, "missing location");
    add(preview.unknown_booking_refs, "unmatched booking");
    add(preview.duplicate_pupil_refs, "duplicate pupil");
    add(preview.invalid_booking_dates, "invalid booking date");
    add(preview.invalid_booking_times, "invalid booking time");
    add(preview.overlapping_bookings, "overlapping booking");
    add(preview.booking_block_conflicts, "diary conflict");
    add(preview.invalid_calendar_blocks, "invalid Away/Gap block");
    add(preview.test_day_issues, "invalid test-day block");
    add(preview.booking_commitment_shortfalls, "pupil below booking commitment");
    add(preview.duplicate_connection_preferences, "duplicate connection choice");
    add(preview.inconsistent_connection_preferences, "inconsistent connection choice");
    if (preview.business_document_pending_count) {
      issues.push(
        `${preview.business_document_pending_count} pending business ${preview.business_document_pending_count === 1 ? "document" : "documents"}`,
      );
    }
    return issues;
  }

  function validationSummary(dataset) {
    const preview = dataset.activation_preview || {};
    if (preview.validation_ready) return "Validation complete · activation remains locked";
    const issueCount = preview.validation_issue_count || validationIssues(dataset).length;
    return issueCount
      ? `Needs review · ${issueCount} ${issueCount === 1 ? "issue" : "issues"}`
      : "Validation not yet complete";
  }

  async function reviewActivationImpact(dataset) {
    setPlanningId(dataset.id);
    try {
      const { data } = await axios.get(
        `${API}/api/pdi-replica/datasets/${dataset.id}/activation-dry-run`,
        { withCredentials: true },
      );
      setActivationPlans((current) => ({ ...current, [dataset.id]: data }));
      toast.success("Activation dry run complete", {
        description: "No live data changed and no invitations were sent.",
      });
    } catch (error) {
      toast.error("Couldn't review activation impact", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setPlanningId(null);
    }
  }

  async function removeDraft(dataset) {
    setDeletingId(dataset.id);
    try {
      await axios.delete(`${API}/api/pdi-replica/datasets/${dataset.id}`, {
        withCredentials: true,
      });
      toast.success("Private onboarding draft removed");
      await refresh();
    } catch (error) {
      toast.error("Couldn't remove onboarding draft", {
        description: error?.response?.data?.detail || error.message,
      });
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  }

  const pupilOptions = draft.pupils.map((pupil) => ({
    value: pupil.external_ref || pupil.name,
    label: pupil.name || "Unnamed pupil",
  }));

  return (
    <>
      <button
        type="button"
        data-testid="open-pdi-replica-lab"
        onClick={() => setOpen(true)}
        className="w-full rounded-xl border border-sky-700/50 bg-sky-950/30 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-sky-200">
          <ShieldCheck className="h-4 w-4" />
          PDI onboarding package
        </span>
        <span className="mt-1 block text-[11px] leading-relaxed text-sky-100/70">
          Build and check the complete setup privately, then activate everyone together.
        </span>
      </button>

      <MobileDialog
        bareContent
        open={open}
        onClose={() => setOpen(false)}
        testid="pdi-replica-lab-modal"
      >
        <div className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-slate-950 text-white sm:max-w-3xl sm:rounded-3xl">
          <header className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
            <div>
              <h2 className="text-lg font-extrabold">Instructor onboarding package</h2>
              <p className="text-xs text-slate-400">
                Prepare and check everything privately. Nothing below contacts a pupil.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close onboarding package"
              onClick={() => setOpen(false)}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="flex-1 space-y-5 overflow-y-auto p-4">
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              {[
                ["Messages", "Disabled"],
                ["Invites", "Disabled"],
                ["Live diary", "Disabled"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-emerald-800/50 bg-emerald-950/30 p-3">
                  <p className="font-bold text-emerald-300">{value}</p>
                  <p className="mt-1 text-emerald-100/60">{label}</p>
                </div>
              ))}
            </div>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-bold text-white">1. Package details</h3>
                {editingId ? (
                  <button
                    type="button"
                    onClick={startFreshDraft}
                    className="rounded-lg border border-slate-700 px-3 py-2 text-[11px] font-bold text-slate-300 hover:bg-slate-800"
                  >
                    Start new package
                  </button>
                ) : null}
              </div>
              {editingId ? (
                <p className="mt-2 rounded-lg border border-sky-800/60 bg-sky-950/30 p-2 text-[11px] font-bold text-sky-200">
                  Continuing saved private draft · activation remains locked
                </p>
              ) : null}
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Package name
                  <input
                    value={draft.label}
                    onChange={(event) => updateDraft("label", event.target.value)}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Evidence summary
                  <input
                    value={draft.source_summary || ""}
                    onChange={(event) => updateDraft("source_summary", event.target.value)}
                    className={inputClass}
                  />
                </label>
              </div>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-white">2. Pupils</h3>
                  <p className="text-xs text-slate-400">Add contacts, locations and their normal lesson pattern.</p>
                </div>
                <button
                  type="button"
                  onClick={addPupil}
                  className="flex h-10 items-center gap-2 rounded-lg bg-sky-600 px-3 text-xs font-bold hover:bg-sky-500"
                >
                  <Plus className="h-4 w-4" /> Add pupil
                </button>
              </div>

              {draft.pupils.length ? (
                <div className="mt-3 space-y-3">
                  {draft.pupils.map((pupil, index) => (
                    <div key={index} className="rounded-xl border border-slate-700 bg-slate-950/60 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold">Pupil {index + 1}</p>
                        <button
                          type="button"
                          aria-label={`Remove pupil ${index + 1}`}
                          onClick={() => removePupil(index)}
                          className="rounded-lg p-2 text-rose-300 hover:bg-rose-950/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <label className={labelClass}>
                          Full name
                          <input value={pupil.name} onChange={(event) => updatePupil(index, "name", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Reference (optional)
                          <input value={pupil.external_ref || ""} onChange={(event) => updatePupil(index, "external_ref", event.target.value)} className={inputClass} placeholder="e.g. louis-cook" />
                        </label>
                        <label className={labelClass}>
                          Phone
                          <input type="tel" value={pupil.phone || ""} onChange={(event) => updatePupil(index, "phone", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Email
                          <input type="email" value={pupil.email || ""} onChange={(event) => updatePupil(index, "email", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Address
                          <input value={pupil.primary_address || ""} onChange={(event) => updatePupil(index, "primary_address", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Postcode
                          <input value={pupil.home_postcode || ""} onChange={(event) => updatePupil(index, "home_postcode", event.target.value.toUpperCase())} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Area
                          <input value={pupil.area_label || ""} onChange={(event) => updatePupil(index, "area_label", event.target.value)} className={inputClass} placeholder="e.g. Wisbech" />
                        </label>
                        <label className={labelClass}>
                          Normal lesson
                          <select value={pupil.normal_duration_minutes || 120} onChange={(event) => updatePupil(index, "normal_duration_minutes", Number(event.target.value))} className={inputClass}>
                            <option value={60}>1 hour</option>
                            <option value={90}>1½ hours</option>
                            <option value={120}>2 hours</option>
                            <option value={150}>2½ hours</option>
                            <option value={180}>3 hours</option>
                          </select>
                        </label>
                      </div>
                      <label className="mt-3 flex items-start gap-2 text-xs text-slate-300">
                        <input type="checkbox" checked={pupil.minimum_booking_exempt} onChange={(event) => updatePupil(index, "minimum_booking_exempt", event.target.checked)} className="mt-0.5" />
                        Exempt from the three-forward-bookings rule
                      </label>
                      {pupil.minimum_booking_exempt ? (
                        <label className={`${labelClass} mt-3 block`}>
                          Exemption reason
                          <input value={pupil.exemption_reason || ""} onChange={(event) => updatePupil(index, "exemption_reason", event.target.value)} className={inputClass} placeholder="e.g. fixed driving test" />
                        </label>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-dashed border-slate-700 p-4 text-center text-sm text-slate-500">
                  No pupils added yet.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-white">3. Future and historical lessons</h3>
                  <p className="text-xs text-slate-400">Test-day duration stays separate from the pupil's normal lesson.</p>
                </div>
                <button
                  type="button"
                  onClick={addBooking}
                  disabled={!draft.pupils.length}
                  className="flex h-10 items-center gap-2 rounded-lg bg-sky-600 px-3 text-xs font-bold hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" /> Add lesson
                </button>
              </div>

              {draft.bookings.length ? (
                <div className="mt-3 space-y-3">
                  {draft.bookings.map((booking, index) => (
                    <div key={index} className="rounded-xl border border-slate-700 bg-slate-950/60 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold">Lesson {index + 1}</p>
                        <button type="button" aria-label={`Remove lesson ${index + 1}`} onClick={() => removeBooking(index)} className="rounded-lg p-2 text-rose-300 hover:bg-rose-950/40">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <label className={labelClass}>
                          Pupil
                          <select value={booking.pupil_ref} onChange={(event) => updateBooking(index, "pupil_ref", event.target.value)} className={inputClass}>
                            <option value="">Select pupil</option>
                            {pupilOptions.map((pupil) => <option key={pupil.value} value={pupil.value}>{pupil.label}</option>)}
                          </select>
                        </label>
                        <label className={labelClass}>
                          Date
                          <input type="date" value={booking.date} onChange={(event) => updateBooking(index, "date", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Start
                          <input type="time" value={booking.start_time} onChange={(event) => updateBooking(index, "start_time", event.target.value)} className={inputClass} />
                        </label>
                        <label className={labelClass}>
                          Duration
                          <select value={booking.duration_minutes} onChange={(event) => updateBooking(index, "duration_minutes", Number(event.target.value))} className={inputClass}>
                            <option value={60}>1 hour</option>
                            <option value={90}>1½ hours</option>
                            <option value={120}>2 hours</option>
                            <option value={180}>3 hours</option>
                            <option value={240}>4 hours</option>
                          </select>
                        </label>
                        <label className={labelClass}>
                          Status
                          <select value={booking.status} onChange={(event) => updateBooking(index, "status", event.target.value)} className={inputClass}>
                            <option value="booked">Booked</option>
                            <option value="completed">Historical / completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </label>
                        <label className="flex items-center gap-2 self-end pb-3 text-xs text-slate-300">
                          <input type="checkbox" checked={booking.is_test_day} onChange={(event) => {
                            updateBooking(index, "is_test_day", event.target.checked);
                            if (event.target.checked) updateBooking(index, "fixed", true);
                          }} />
                          Fixed test-day block
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-dashed border-slate-700 p-4 text-center text-sm text-slate-500">
                  Add pupils first, then add their lessons.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-white">4. Away and diary gaps</h3>
                  <p className="text-xs text-slate-400">
                    Preserve days off and intentional gaps without putting them in the live diary.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addCalendarBlock}
                  className="flex h-10 items-center gap-2 rounded-lg bg-sky-600 px-3 text-xs font-bold hover:bg-sky-500"
                >
                  <Plus className="h-4 w-4" /> Add block
                </button>
              </div>

              {(draft.calendar_blocks || []).length ? (
                <div className="mt-3 space-y-3">
                  {(draft.calendar_blocks || []).map((block, index) => (
                    <div key={index} className="rounded-xl border border-slate-700 bg-slate-950/60 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold">Diary block {index + 1}</p>
                        <button
                          type="button"
                          aria-label={`Remove diary block ${index + 1}`}
                          onClick={() => removeCalendarBlock(index)}
                          className="rounded-lg p-2 text-rose-300 hover:bg-rose-950/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <label className={labelClass}>
                          Block type
                          <select
                            value={block.block_type}
                            onChange={(event) => {
                              const type = event.target.value;
                              updateCalendarBlock(index, "block_type", type);
                              updateCalendarBlock(index, "all_day", type === "away");
                            }}
                            className={inputClass}
                          >
                            <option value="away">Away / day off</option>
                            <option value="gap">Protected diary gap</option>
                          </select>
                        </label>
                        <label className={labelClass}>
                          Label
                          <input
                            value={block.label || ""}
                            onChange={(event) => updateCalendarBlock(index, "label", event.target.value)}
                            className={inputClass}
                            placeholder={block.block_type === "away" ? "e.g. Holiday" : "e.g. Travel gap"}
                          />
                        </label>
                        <label className={labelClass}>
                          Start date
                          <input
                            type="date"
                            value={block.start_date}
                            onChange={(event) => updateCalendarBlock(index, "start_date", event.target.value)}
                            className={inputClass}
                          />
                        </label>
                        <label className={labelClass}>
                          End date
                          <input
                            type="date"
                            value={block.end_date || ""}
                            onChange={(event) => updateCalendarBlock(index, "end_date", event.target.value)}
                            className={inputClass}
                          />
                        </label>
                        {!block.all_day ? (
                          <>
                            <label className={labelClass}>
                              Start time
                              <input
                                type="time"
                                value={block.start_time || ""}
                                onChange={(event) => updateCalendarBlock(index, "start_time", event.target.value)}
                                className={inputClass}
                              />
                            </label>
                            <label className={labelClass}>
                              End time
                              <input
                                type="time"
                                value={block.end_time || ""}
                                onChange={(event) => updateCalendarBlock(index, "end_time", event.target.value)}
                                className={inputClass}
                              />
                            </label>
                          </>
                        ) : null}
                      </div>
                      <label className="mt-3 flex items-center gap-2 text-xs text-slate-300">
                        <input
                          type="checkbox"
                          checked={block.all_day}
                          onChange={(event) => updateCalendarBlock(index, "all_day", event.target.checked)}
                        />
                        All-day block
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-dashed border-slate-700 p-4 text-center text-sm text-slate-500">
                  No Away or Gap blocks added yet.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-white">5. Business records</h3>
                  <p className="text-xs text-slate-400">
                    Add opening balances, income and expenses. Uploaded evidence is private to you.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addBusinessRecord}
                  className="flex h-10 items-center gap-2 rounded-lg bg-sky-600 px-3 text-xs font-bold hover:bg-sky-500"
                >
                  <Plus className="h-4 w-4" /> Add record
                </button>
              </div>

              {(draft.business_records || []).length ? (
                <div className="mt-3 space-y-3">
                  {(draft.business_records || []).map((record, index) => (
                    <div key={index} className="rounded-xl border border-slate-700 bg-slate-950/60 p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold">Business record {index + 1}</p>
                        <button
                          type="button"
                          aria-label={`Remove business record ${index + 1}`}
                          onClick={() => removeBusinessRecord(index)}
                          className="rounded-lg p-2 text-rose-300 hover:bg-rose-950/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 grid gap-3 sm:grid-cols-2">
                        <label className={labelClass}>
                          Record type
                          <select
                            value={record.record_type}
                            onChange={(event) => updateBusinessRecord(index, "record_type", event.target.value)}
                            className={inputClass}
                          >
                            <option value="opening_balance">Opening balance</option>
                            <option value="income">Income</option>
                            <option value="expense">Expense</option>
                            <option value="receipt">Receipt</option>
                            <option value="bank_statement">Bank statement</option>
                            <option value="paperwork">Other paperwork</option>
                          </select>
                        </label>
                        <label className={labelClass}>
                          Date
                          <input
                            type="date"
                            value={record.date || ""}
                            onChange={(event) => updateBusinessRecord(index, "date", event.target.value)}
                            className={inputClass}
                          />
                        </label>
                        <label className={labelClass}>
                          Amount (£)
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={record.amount_pence == null ? "" : (record.amount_pence / 100)}
                            onChange={(event) => updateBusinessRecord(
                              index,
                              "amount_pence",
                              event.target.value === "" ? null : Math.round(Number(event.target.value) * 100),
                            )}
                            className={inputClass}
                            placeholder="0.00"
                          />
                        </label>
                        <label className={labelClass}>
                          Description
                          <input
                            value={record.description || ""}
                            onChange={(event) => updateBusinessRecord(index, "description", event.target.value)}
                            className={inputClass}
                            placeholder="e.g. August bank statement"
                          />
                        </label>
                      </div>

                      <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/70 p-3">
                        <input
                          ref={(node) => { businessFileInputRefs.current[index] = node; }}
                          type="file"
                          accept=".pdf,.csv,.txt,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx,.xls,.xlsx"
                          onChange={(event) => selectBusinessFile(index, event.target.files?.[0])}
                          className="block w-full text-xs text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-700 file:px-3 file:py-2 file:font-bold file:text-white"
                        />
                        <div className="mt-3 flex items-center justify-between gap-3">
                          <p className="min-w-0 truncate text-[11px] text-slate-400">
                            {record.document_status === "uploaded"
                              ? `Private upload complete · ${record.original_filename}`
                              : businessFiles[index]?.name || "Document optional"}
                          </p>
                          <button
                            type="button"
                            onClick={() => uploadBusinessDocument(index)}
                            disabled={!businessFiles[index] || uploadingRecord === index}
                            className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-emerald-700 px-3 text-xs font-bold text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            <FileUp className="h-4 w-4" />
                            {uploadingRecord === index ? "Uploading…" : "Upload privately"}
                          </button>
                        </div>
                        <p className="mt-2 text-[10px] leading-relaxed text-emerald-300/80">
                          Owner-only. Uploading does not send messages, invite pupils or write to the live diary.
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-3 rounded-lg border border-dashed border-slate-700 p-4 text-center text-sm text-slate-500">
                  Business records are optional and can be added before activation.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div>
                <h3 className="font-bold text-white">6. Optional connections</h3>
                <p className="text-xs text-slate-400">
                  Choose what to prepare. This records your setup choices only—it does not connect, send, publish or spend.
                </p>
              </div>
              <div className="mt-3 space-y-2">
                {CONNECTION_OPTIONS.map((option) => {
                  const preference = (draft.connection_preferences || []).find(
                    (item) => item.connection_type === option.connection_type,
                  );
                  const requested = preference?.setup_requested === true;
                  return (
                    <label
                      key={option.connection_type}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                        requested
                          ? "border-sky-700/70 bg-sky-950/30"
                          : "border-slate-800 bg-slate-950/50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={requested}
                        onChange={(event) => updateConnectionPreference(
                          option.connection_type,
                          event.target.checked,
                        )}
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-bold text-white">
                          {option.label}
                        </span>
                        <span className="mt-1 block text-[11px] leading-relaxed text-slate-400">
                          {option.description}
                        </span>
                        <span className={`mt-1 block text-[10px] font-bold ${
                          requested ? "text-sky-300" : "text-slate-600"
                        }`}>
                          {requested ? "Requested for setup · not connected" : "Not selected"}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-3 rounded-lg border border-amber-800/50 bg-amber-950/20 p-3 text-[10px] leading-relaxed text-amber-200">
                Every real connection will require its own permission screen. Advertising spend, outbound messages and pupil invitations always require separate approval.
              </p>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <button type="button" onClick={openAdvancedJson} className="text-xs font-bold text-sky-300 hover:text-sky-200">
                Advanced import / JSON
              </button>
              {advancedOpen ? (
                <div className="mt-3">
                  <textarea
                    data-testid="pdi-replica-json"
                    value={payload}
                    onChange={(event) => setPayload(event.target.value)}
                    spellCheck={false}
                    className="min-h-64 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 font-mono text-xs text-slate-100 outline-none focus:border-sky-500"
                  />
                  <div className="mt-2 flex gap-2">
                    <Button type="button" onClick={applyAdvancedJson} className="bg-sky-600 hover:bg-sky-500">Apply data</Button>
                    <Button type="button" variant="outline" onClick={() => setAdvancedOpen(false)}>Cancel</Button>
                  </div>
                </div>
              ) : null}
            </section>

            <Button
              data-testid="save-pdi-replica"
              onClick={saveReplica}
              disabled={saving}
              className="h-12 w-full rounded-xl bg-sky-600 font-bold text-white hover:bg-sky-500"
            >
              {saving
                ? (editingId ? "Updating private package…" : "Saving private package…")
                : (editingId ? "Update private onboarding package" : "Save private onboarding package")}
            </Button>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">
                Saved onboarding packages
              </h3>
              {loading ? (
                <p className="py-3 text-sm text-slate-500">Loading…</p>
              ) : datasets.length ? (
                <div className="mt-2 space-y-2">
                  {datasets.map((dataset) => (
                    <div key={dataset.id} className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
                      <p className="font-bold text-white">{dataset.label}</p>
                      <p className="mt-1 text-xs text-slate-400">
                        {dataset.pupil_count} pupils · {dataset.booking_count} bookings ·{" "}
                        {dataset.calendar_block_count || 0} blocks ·{" "}
                        {dataset.business_record_count || 0} business records ·{" "}
                        {dataset.requested_connection_count || 0} connections requested
                      </p>
                      <p className="mt-1 text-[11px] text-emerald-400">
                        Draft · isolated · communications off · live diary off
                      </p>
                      <p className="mt-2 text-[11px] font-medium text-amber-300">
                        {validationSummary(dataset)}
                      </p>
                      {!dataset.activation_preview?.validation_ready ? (
                        <ul className="mt-2 space-y-1 text-[10px] leading-relaxed text-amber-100/80">
                          {validationIssues(dataset).map((issue) => (
                            <li key={issue}>• {issue}</li>
                          ))}
                        </ul>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => continueDraft(dataset)}
                        disabled={loading}
                        className="mt-3 h-10 w-full rounded-lg border border-sky-700/60 bg-sky-950/30 text-xs font-bold text-sky-200 hover:bg-sky-950/60 disabled:opacity-50"
                      >
                        Continue setup
                      </button>
                      <button
                        type="button"
                        onClick={() => reviewActivationImpact(dataset)}
                        disabled={planningId === dataset.id}
                        className="mt-2 h-10 w-full rounded-lg border border-emerald-800/70 bg-emerald-950/20 text-xs font-bold text-emerald-200 hover:bg-emerald-950/40 disabled:opacity-50"
                      >
                        {planningId === dataset.id
                          ? "Checking live impact…"
                          : "Review activation impact"}
                      </button>
                      {activationPlans[dataset.id] ? (
                        <div className="mt-2 rounded-lg border border-slate-700 bg-slate-950/70 p-3">
                          <p className="text-xs font-bold text-white">
                            {activationPlans[dataset.id].plan_clear
                              ? "Dry run clear"
                              : "Dry run needs attention"}
                          </p>
                          <p className="mt-1 text-[10px] leading-relaxed text-slate-400">
                            {activationPlans[dataset.id].impact.pupils_to_create} pupils to create ·{" "}
                            {activationPlans[dataset.id].impact.pupils_to_restore} to restore ·{" "}
                            {activationPlans[dataset.id].impact.bookings_to_create} bookings ·{" "}
                            {activationPlans[dataset.id].impact.calendar_blocks_to_create} blocks ·{" "}
                            {activationPlans[dataset.id].impact.manual_invites_to_prepare} manual invites to prepare
                          </p>
                          {(activationPlans[dataset.id].blocking_reasons || []).length ? (
                            <ul className="mt-2 space-y-1 text-[10px] text-amber-200">
                              {activationPlans[dataset.id].blocking_reasons.map((reason) => (
                                <li key={reason}>• {reason}</li>
                              ))}
                            </ul>
                          ) : null}
                          <p className="mt-2 rounded-md bg-emerald-950/30 p-2 text-[10px] leading-relaxed text-emerald-200">
                            Dry run only · 0 writes · 0 messages · 0 invites sent · 0 connections · £0 spend
                          </p>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        disabled
                        title="Activation stays locked until write-journal, delivery and rollback checks pass."
                        className="mt-3 h-10 w-full cursor-not-allowed rounded-lg bg-slate-800 text-xs font-bold text-slate-500"
                      >
                        Activate pupils & prepare initial invites
                      </button>
                      {pendingDelete?.id === dataset.id ? (
                        <div
                          role="alertdialog"
                          aria-label={`Confirm removal of ${dataset.label}`}
                          className="mt-2 rounded-lg border border-rose-800/70 bg-rose-950/30 p-3"
                        >
                          <p className="text-xs font-bold text-rose-200">
                            Remove “{dataset.label}”?
                          </p>
                          <p className="mt-1 text-[11px] leading-relaxed text-rose-100/70">
                            This removes only this private draft. Live pupils, bookings,
                            messages and invitations cannot be affected.
                          </p>
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setPendingDelete(null)}
                              disabled={deletingId === dataset.id}
                              className="h-10 rounded-lg border border-slate-700 text-xs font-bold text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                            >
                              Keep draft
                            </button>
                            <button
                              type="button"
                              data-testid="confirm-remove-private-draft"
                              onClick={() => removeDraft(dataset)}
                              disabled={deletingId === dataset.id}
                              className="h-10 rounded-lg bg-rose-700 text-xs font-bold text-white hover:bg-rose-600 disabled:opacity-50"
                            >
                              {deletingId === dataset.id ? "Removing…" : "Yes, remove"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPendingDelete(dataset)}
                          disabled={deletingId === dataset.id}
                          className="mt-2 h-10 w-full rounded-lg border border-rose-900/70 bg-rose-950/20 text-xs font-bold text-rose-300 hover:bg-rose-950/40 disabled:opacity-50"
                        >
                          Remove private draft
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-3 text-sm text-slate-500">No onboarding packages saved yet.</p>
              )}
            </section>
          </div>
        </div>
      </MobileDialog>
    </>
  );
}
